<template>
  <div class="relative min-h-screen overflow-hidden">
    <!-- Background image (Unsplash) -->
    <div class="absolute inset-0 bg-cover bg-center" :style="{ backgroundImage: `url(${backgroundUrl})` }" />
    <div class="absolute inset-0 bg-gradient-to-br from-slate-950/75 via-slate-900/50 to-slate-900/75" />

    <!-- Content -->
    <div class="relative min-h-screen flex items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
      <div class="w-full max-w-md">
        <div class="relative rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl ring-1 ring-black/5">
              <!-- Server menu -->
          <div ref="serverMenuRoot" class="absolute right-4 top-4">
            <button
              type="button"
              class="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              aria-label="Open settings"
              @click="toggleServerMenu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-5 w-5">
                <path d="M10 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM10 11.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM10 16.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
              </svg>
            </button>

            <div
              v-if="showServerMenu"
              class="absolute right-0 mt-2 w-72 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/10"
            >
              <button
                type="button"
                class="flex w-full items-start gap-3 px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50"
                @click="changeServerAddress"
              >
                <span class="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
                    <path d="M4 6.75A3.75 3.75 0 0 1 7.75 3h8.5A3.75 3.75 0 0 1 20 6.75v1.5A3.75 3.75 0 0 1 16.25 12h-8.5A3.75 3.75 0 0 1 4 8.25v-1.5Zm3.75-.75a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H7.75Zm3 0a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H10.75Z" />
                    <path d="M4 15.75A3.75 3.75 0 0 1 7.75 12h8.5A3.75 3.75 0 0 1 20 15.75v1.5A3.75 3.75 0 0 1 16.25 21h-8.5A3.75 3.75 0 0 1 4 17.25v-1.5Zm3.75-.75a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H7.75Zm3 0a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H10.75Z" />
                  </svg>
                </span>
                <span class="min-w-0">
                  <div class="font-medium">{{ $t('login.change_server') }}</div>
                  <div class="mt-0.5 text-xs text-slate-500">Switch API/database endpoint</div>
                </span>
              </button>
            </div>
          </div>

          <div class="px-6 py-8 sm:px-10">
            <!-- Branding -->
            <div class="flex flex-col items-center">
              <img :src="require('@/assets/logo/icon-geebung.png')" alt="Salamanda ERP" class="h-16 w-auto" />
              <h1 class="mt-5 text-center text-2xl font-semibold tracking-tight text-slate-900">
                {{ $t('login.welcome_heading') }}
              </h1>
              <p class="mt-2 text-center text-sm leading-6 text-slate-600">
                {{ $t('login.welcome_description') }}
              </p>
            </div>

            <!-- Error -->
            <div
              v-if="auth_error && auth_message"
              class="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              role="alert"
            >
              {{ auth_message }}
            </div>

            <!-- Form -->
            <form class="mt-6 space-y-4" @submit.prevent="login">
              <!-- Username -->
              <div>
                <label class="block text-sm font-medium text-slate-700">{{ $t('login.username') }}</label>
                <div class="relative mt-1">
                  <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
                      <path fill-rule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clip-rule="evenodd" />
                    </svg>
                  </span>
                  <input
                    ref="username"
                    v-model.trim="data.username"
                    class="block w-full rounded-xl border border-slate-300 bg-white/90 py-3 pl-10 pr-3 text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    type="text"
                    :placeholder="$t('login.username')"
                    autocomplete="username"
                    required
                    @keyup.enter.prevent="focusPass"
                  />
                </div>
                </div>

              <!-- Password -->
              <div>
                <label class="block text-sm font-medium text-slate-700">{{ $t('login.password') }}</label>
                <div class="relative mt-1">
                  <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
                      <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3A3 3 0 0 0 3.75 12v6.75A3.75 3.75 0 0 0 7.5 22.5h9a3.75 3.75 0 0 0 3.75-3.75V12a3 3 0 0 0-3-3V6.75A5.25 5.25 0 0 0 12 1.5Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
                    </svg>
                  </span>

                  <input
                    ref="password"
                    v-model="data.password"
                    class="block w-full rounded-xl border border-slate-300 bg-white/90 py-3 pl-10 pr-10 text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    :type="showPassword ? 'text' : 'password'"
                    :placeholder="$t('login.password')"
                    autocomplete="current-password"
                      required
                      @keyup.enter="login"
                  />

                  <button
                    type="button"
                    class="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700 focus:outline-none"
                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    @click="showPassword = !showPassword"
                  >
                    <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
                      <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l2.05 2.05A12.132 12.132 0 0 0 1.5 12c2.309 4.19 6.71 7.5 10.5 7.5a10.43 10.43 0 0 0 4.334-.988l2.136 2.136a.75.75 0 1 0 1.06-1.06l-18-18Z" />
                      <path d="M10.312 7.524 8.56 5.772A10.64 10.64 0 0 1 12 4.5c3.79 0 8.191 3.31 10.5 7.5a12.07 12.07 0 0 1-3.358 3.92l-1.564-1.564a5.25 5.25 0 0 0-7.266-6.832Z" />
                      <path d="M15.195 12.973 11.027 8.805a3.75 3.75 0 0 1 4.168 4.168Z" />
                      <path d="M8.805 11.027a3.75 3.75 0 0 0 4.168 4.168l-4.168-4.168Z" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
                      <path d="M12 4.5c-3.79 0-8.191 3.31-10.5 7.5C3.809 16.19 8.21 19.5 12 19.5s8.191-3.31 10.5-7.5C20.191 7.81 15.79 4.5 12 4.5Z" />
                      <path d="M12 8.25a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center justify-between">
                <label class="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    v-model="rememberMe"
                    type="checkbox"
                    class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30"
                  />
                  <span>Remember me</span>
                </label>

                <button
                  type="button"
                  class="text-sm font-medium text-blue-700 hover:text-blue-800"
                  @click="forgotPassword"
                >
                  {{ $t('link.pw_reset') }}
                </button>
              </div>

              <!-- Submit -->
              <button
                type="submit"
                class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!data.username || !data.password || loading"
              >
                <svg
                  v-if="loading"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="h-5 w-5 animate-spin"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
                </svg>
                <span>{{ $t('login.button') }}</span>
              </button>
            </form>

            <!-- Secondary action -->
            <div class="mt-6">
              <!-- <div class="relative">
                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                  <div class="w-full border-t border-slate-200" />
                </div>
                <div class="relative flex justify-center">
                  <span class="bg-white/95 px-2 text-xs text-slate-500">
                    {{ $t('login.system_description') }}
                  </span>
                </div>
                      </div>

              <button
                type="button"
                class="mt-4 inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                @click="getOffer"
              >
                Get Offer
              </button> -->

              <p class="mt-6 text-center text-xs text-slate-500">
                {{ $t('login.copyright') }}
              </p>
                        </div>
                      </div>
                    </div>

        <!-- <p class="mt-4 text-center text-xs text-white/70">
          Background photo: <a class="underline hover:text-white" :href="unsplashPageUrl" target="_blank" rel="noreferrer">Unsplash</a>
        </p> -->
                    </div>
              </div>
            </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Login',
  data () {
    return {
      data: {
        username: '',
        password: ''
      },
      rememberMe: true,
      showPassword: false,
      showServerMenu: false,
      auth_message: '',
      // Unsplash (stable id URL; set explicit crop to keep it crisp on large displays)
      backgroundUrl: 'https://images.unsplash.com/photo-1519455953755-af066f52f1ea?auto=format&fit=crop&w=2400&q=80',
      unsplashPageUrl: 'https://unsplash.com/photos/af066f52f1ea'
    }
  },
  computed: {
    ...mapState({
      loading: state => state.Auth.status === 'loading',
      auth_error: state => state.Auth.status === 'error',
      name: state => state.Auth.user.name,
      last_login: state => state.Auth.user.last_login,
      auth_status: state => state.Auth.status,
      intended: state => state.Auth.intended
    })
  },
  watch: {
    auth_status (value) {
      if (value !== 'error') return
      if (!this.auth_message) this.auth_message = this.$t('login.error.message')
    }
  },
  methods: {
    ...mapActions('Base', ['setBaseURL']),
    ...mapActions('Auth', ['setIntended']),
    toggleServerMenu () {
      this.showServerMenu = !this.showServerMenu
    },
    onDocumentClick (e) {
      if (!this.showServerMenu) return
      const menuRoot = this.$refs.serverMenuRoot
      if (menuRoot && menuRoot.contains && menuRoot.contains(e.target)) return
      this.showServerMenu = false
    },
    login () {
      this.auth_message = ''
      this.$authService.login(Object.assign({}, this.data))
        .then(res => {
          this.setLanguage(res.user.lang || 'en')

          const displayName = (res && res.user && res.user.name) || this.name
          const lastLogin = (res && res.user && res.user.last_login) || this.last_login
          this.$notify({
            title: this.$t('status.success'),
            message: this.$t('login.welcome', [displayName, lastLogin]),
            type: 'success'
          })

          if (this.intended) {
            this.$router.push({ name: this.intended })
          } else {
            this.$router.push({ name: 'dashboard' })
          }
        })
        .catch(err => {
          if (err && err.response) {
            const status = err.response.status
            if (status === 500) {
              this.auth_message = this.$t('login.error.server')
            } else if (status === 401) {
              this.auth_message = (err.response.data && err.response.data.error) || this.$t('login.error.message')
            } else {
              this.auth_message = this.$t('login.error.message')
            }
          } else {
            this.auth_message = this.$t('login.error.server')
          }
        })
    },
    focusPass () {
      if (this.$refs.password && this.$refs.password.focus) this.$refs.password.focus()
    },
    changeServerAddress () {
      this.showServerMenu = false
      this.setBaseURL(null)
      this.setIntended(null)
      this.$router.push({ name: 'base' })
    },
    forgotPassword () {
      // There is no dedicated password-reset route in this app; keep the UX safe/non-breaking.
      this.displayMessage(this.$t('message.coming'), () => {}, 'info', this.$t('link.pw_reset'))
    },
    getOffer () {
      this.displayMessage(this.$t('message.coming'))
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.username && this.$refs.username.focus) this.$refs.username.focus()
    })
    document.addEventListener('click', this.onDocumentClick, true)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick, true)
  }
}
</script>
